import Flex from "@/components/Flex";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import {
  PARTICIPANT_NOTE_STATE,
  PARTICIPANT_NOTE_STATE_LABEL,
  SESSION_NOTE_LABEL,
} from "../group-visit-constant";
import { SessionParticipant } from "../group-visit-types";

export type SessionNoteBreadcrumbProps = {
  /** Participant whose note is open; absent on the shared note. */
  participant?: SessionParticipant;
  isSaving: boolean;
  onOpenSharedNote: () => void;
};

/**
 * "Shared Group Note / <patient>" trail above the note.
 */
const SessionNoteBreadcrumb = ({
  participant,
  isSaving,
  onOpenSharedNote,
}: SessionNoteBreadcrumbProps) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") onOpenSharedNote();
  };

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      sx={{
        flexShrink: 0,
        px: 2,
        py: 1,
        backgroundColor: CHART_COLORS.surfaceWhite,
        borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
      }}
    >
      <Flex alignItems="center" gap={1} flexWrap="wrap">
        <Flex
          alignItems="center"
          gap={1}
          role={participant ? "button" : undefined}
          tabIndex={participant ? 0 : undefined}
          onClick={participant ? onOpenSharedNote : undefined}
          onKeyDown={participant ? handleKeyDown : undefined}
          sx={{ cursor: participant ? "pointer" : "default" }}
        >
          <Text
            {...CHART_TYPOGRAPHY.body4}
            fontWeight={CHART_TYPOGRAPHY.weight.medium}
            c={
              participant
                ? CHART_COLORS.btnSecondaryText
                : CHART_COLORS.textDarker
            }
          >
            {SESSION_NOTE_LABEL.sharedNoteTab}
          </Text>
        </Flex>

        {!!participant && (
          <>
            <Text {...CHART_TYPOGRAPHY.body4} c={CHART_COLORS.textMutedGrey}>
              /
            </Text>
            <Text
              {...CHART_TYPOGRAPHY.body4}
              fontWeight={CHART_TYPOGRAPHY.weight.medium}
              c={CHART_COLORS.textDarker}
            >
              {participant.name}
            </Text>
            <StatusChip
              label={PARTICIPANT_NOTE_STATE_LABEL[participant.noteState]}
              variant={
                participant.noteState === PARTICIPANT_NOTE_STATE.COMPLETE
                  ? "success"
                  : "warning"
              }
            />
          </>
        )}
      </Flex>

      {isSaving && (
        <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.badgeWarningText}>
          {SESSION_NOTE_LABEL.saving}
        </Text>
      )}
    </Flex>
  );
};

export default SessionNoteBreadcrumb;
